import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { BatteryCharging, FileText, Clock, Wrench } from "lucide-react";
import { Card } from "@/components/ui/card";
import Loader from "@/components/Loader";
import { STATUS_META, RESOLUTION_LABELS } from "@/lib/constants";
import api from "@/lib/apiClient";

const COLORS = ["#059669", "#3b82f6", "#8b5cf6", "#f59e0b", "#6366f1", "#ef4444", "#14b8a6", "#ec4899"];

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/reports/summary").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Loader label="Building reports..." />;

  const statusData = data.by_status.map((s) => ({ name: STATUS_META[s.name]?.label || s.name, value: s.value }));
  const resData = data.by_resolution.map((s) => ({ name: RESOLUTION_LABELS[s.name] || s.name, value: s.value }));

  const kpis = [
    { icon: BatteryCharging, label: "Batteries", value: data.total_batteries, tint: "bg-emerald-100 text-emerald-700" },
    { icon: FileText, label: "Total Claims", value: data.total_claims, tint: "bg-blue-100 text-blue-700" },
    { icon: Clock, label: "Open Claims", value: data.open_claims, tint: "bg-amber-100 text-amber-700" },
    { icon: Wrench, label: "Avg TAT (days)", value: data.avg_tat, tint: "bg-violet-100 text-violet-700" },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Reports & Analytics</h1>
        <p className="text-muted-foreground mt-1">Warranty operations at a glance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${k.tint}`}><k.icon className="h-5 w-5" /></div>
            <p className="mt-3 font-display text-3xl font-extrabold">{k.value}</p>
            <p className="text-sm text-muted-foreground">{k.label}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Claims vs Registrations (Monthly)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip /><Legend />
              <Line type="monotone" dataKey="claims" stroke="#3b82f6" strokeWidth={2} />
              <Line type="monotone" dataKey="registrations" stroke="#059669" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Claims by Status</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} /><Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Resolution Breakdown</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={resData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {resData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Top Issue Types</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_issue} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
              <Tooltip />
              <Bar dataKey="value" fill="#059669" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
